import { getDefaultPropertyValue } from "./properties.js";
import { expressions } from "../utils/expressions.js";
import { validation } from "./validation.js";
import { logs } from "./logs.js";
import { nodes } from "./nodes.js";
import { properties } from "./properties.js";
import { subscribe } from "./subscriptions.js";

/**
 * Replaces relational keywords ('this', 'parent') with actual UIDs in expressions
 */
function replaceRelationalKeyword(config, value) {
  if (!expressions.isLinked(value) && !expressions.isSnapshot(value)) {
    return value;
  }

  const specialChars = [
    expressions.getLinkedPropertySpecialChar(),
    expressions.getSnapshotSpecialChar(),
  ];

  let expression = value;

  for (let char of specialChars) {
    if (value.includes("this")) {
      const propertyRegExp = _generateRelationalPropertyRegExp(char, "this");
      const propertyReplacement = _generatePropertyBeginning(char, config.uid);

      expression = expression.replace(propertyRegExp, propertyReplacement);
    }

    if (value.includes("parent")) {
      const propertyRegExp = _generateRelationalPropertyRegExp(char, "parent");
      const propertyReplacement = _generatePropertyBeginning(char, config.parentUid);

      expression = expression.replace(propertyRegExp, propertyReplacement);
    }

    if (value.includes("grandparent")) {
      const parentNode = nodes.get(config.parentUid);
      const grandparentUid = parentNode?.props?.parentUid;

      if (grandparentUid) {
        const propertyRegExp = _generateRelationalPropertyRegExp(char, "grandparent");
        const propertyReplacement = _generatePropertyBeginning(char, grandparentUid);

        expression = expression.replace(propertyRegExp, propertyReplacement);
      }
    }
  }

  return expression;
}

// Function to generate a regular expression for relational property replacement
function _generateRelationalPropertyRegExp(character, relationalProperty = 'parent') {
  return new RegExp(`\\${character}{${relationalProperty}.`, "g");
}

// Function to generate the beginning of a property with a given character and UID
function _generatePropertyBeginning(character, uid) {
  return `${character}{${uid}.`;
}

/**
 * Evaluates a UID that may contain dynamic expressions
 */
const evaluateUid = (uid, event) => {
  if (expressions.isNonReactive(uid)) {
    return properties.evaluateNonReactiveExpression(uid, event);
  }

  return uid;
};

/**
 * Evaluates a string property that may contain dynamic expressions
 */
const evaluateString = (config, event, propertyValue, propertyDescriptor, options = {}) => {
  const { isArrayItem = false, context = {} } = options;
  const defaultValue = propertyDescriptor ? getDefaultPropertyValue(propertyDescriptor) : null;

  if (expressions.isNonReactive(propertyValue)) {
    const evaluatedValue = properties.evaluateNonReactiveExpression(
      propertyValue,
      event,
      defaultValue,
    );

    // Result is still a string for a non-string/non-expression type — partially resolved expression, return as-is
    if (
      typeof evaluatedValue === "string" &&
      propertyDescriptor?.type !== "string" &&
      propertyDescriptor?.type !== "expression"
    ) {
      return evaluatedValue;
    }

    const { isValid } = validation.validateProperty(
      evaluatedValue,
      propertyDescriptor,
      isArrayItem,
      context,
    );

    return isValid ? evaluatedValue : defaultValue;
  }
  const { value: evaluatedValue, evaluatedPropertyUids: publisherPropertiesUids } =
    properties.evaluateLinkedExpression(propertyValue, propertyDescriptor, context);

  if (config?.uid) {
    const propertyUid = `${config?.uid}.${propertyDescriptor?.name}`;

    for (const publisherPropertyUid of publisherPropertiesUids) {
      subscribe(publisherPropertyUid, propertyUid, propertyValue);
    }
  } else {
    // Direct logs usage: This is a module-level utility function, not an entity or singleton class.
    // Using logs directly is acceptable here since there's no component context to bind to.
    logs.warn("dynamic", `This value can not be linked: ${propertyValue}`);
  }

  // Result is still a string for a non-string/non-expression type — partially resolved expression, return as-is
  if (
    typeof evaluatedValue === "string" &&
    propertyDescriptor?.type !== "string" &&
    propertyDescriptor?.type !== "expression"
  ) {
    return evaluatedValue;
  }

  const { isValid } = validation.validateProperty(evaluatedValue, propertyDescriptor, false, context);

  return isValid ? evaluatedValue : defaultValue;
};

/**
 * Evaluates a linked expression (property reference)
 */
const evaluateLinkedExpression = (expression, propertyDescriptor, context = {}) => {
  return properties.evaluateLinkedExpression(expression, propertyDescriptor, context);
};

/**
 * Validates a property value against its descriptor
 * 
 * This function provides a centralized validation mechanism through the evaluation
 * module, eliminating direct dependencies on the validation module.
 */
const validateProperty = (value, propertyDescriptor, isArrayItem = false, context = {}) => {
  return validation.validateProperty(value, propertyDescriptor, isArrayItem, context);
};

// Export all functions used outside this file
export {
  evaluateString,
  evaluateUid,
  replaceRelationalKeyword,
  evaluateLinkedExpression,
  validateProperty
};